import type { RouteLocationRaw } from 'vue-router';

/**
 * Removes redundant location params (cities, states, macroregions) from a path's
 * query string and strips the first region, which is already encoded in the URL slug.
 * Use this after getPathFromFilters when you need a clean URL for navigation.
 */
export function cleanLocationQueryFromPath(path: string): string {
	const parts = path.split('?');
	const base = parts[0] ?? path;
	const qs = parts[1];
	if (!qs) return path;

	const params = new URLSearchParams(qs);

	params.delete('cities');
	params.delete('states');
	params.delete('macroregions');

	const regions = params.get('regions')?.split(',').filter(Boolean) ?? [];
	const remainingRegions = regions.slice(1);

	if (remainingRegions.length === 0) {
		params.delete('regions');
	} else {
		params.set('regions', remainingRegions.join(','));
	}

	const cleaned = params.toString();
	return cleaned ? `${base}?${cleaned}` : base;
}

/**
 * Same as cleanLocationQueryFromPath but accepts RouteLocationRaw (e.g. from getRouteObjectFromFilters).
 */
export function cleanLocationQueryFromRoute(
	route: RouteLocationRaw
): RouteLocationRaw {
	if (typeof route === 'string') return cleanLocationQueryFromPath(route);
	if (!('path' in route) || !route.path) return route;

	const query: Record<string, any> = route.query ? { ...route.query } : {};

	delete query.cities;
	delete query.states;
	delete query.macroregions;

	const regionsValue = query.regions;
	const regions =
		typeof regionsValue === 'string'
			? regionsValue.split(',').filter(Boolean)
			: [];
	const remainingRegions = regions.slice(1);

	if (remainingRegions.length === 0) {
		delete query.regions;
	} else {
		query.regions = remainingRegions.join(',');
	}

	const hasQuery = Object.keys(query).length > 0;
	return hasQuery ? { path: route.path, query } : { path: route.path };
}
